// src/components/atomic-crm/dashboard/Dashboard.tsx
import { useState, useEffect, useMemo } from "react";
import { useGetList, useGetIdentity, useLocale } from "ra-core";
import { useNavigate, Navigate } from "react-router-dom";
import { supabase } from "../providers/supabase/supabase";
import LeadsTable from "./LeadsTable";
import {
  Users,
  DollarSign,
  FileText,
  TrendingUp,
  TrendingDown,
  Plus,
  Clock,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ds/ui/card";
import { Button } from "@/components/ds/ui/button";
import { Badge } from "@/components/ds/ui/badge";
import { Skeleton } from "@/components/ds/ui/skeleton";
import { cn } from "@/lib/utils";
import { DailyBriefing } from "./DailyBriefing";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { format } from "date-fns";
import { getDateFnsLocale } from "@/i18n/date-fns";

const formatDueDate = (dateStr: string | null | undefined, locale: string) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return hasTime
    ? format(d, "PPp", { locale: getDateFnsLocale(locale as any) })
    : format(d, "PP", { locale: getDateFnsLocale(locale as any) });
};

const STAGE_COLORS: Record<string, string> = {
  opportunity: "bg-blue-500",
  "proposal-sent": "bg-violet-500",
  "in-negotiation": "bg-amber-500",
  "in-negociation": "bg-amber-500",
  delayed: "bg-orange-500",
  won: "bg-emerald-500",
  lost: "bg-red-500",
};
const stageColor = (stage: string) => STAGE_COLORS[stage] ?? "bg-primary";

const CLOSED_STAGES = new Set(["won", "closed-won", "won-closed"]);
const LOST_STAGES = new Set(["lost", "closed-lost", "lost-closed"]);

export default function Dashboard() {
  const navigate = useNavigate();
  const { dealStages } = useConfigurationContext();
  const locale = useLocale();

  const { identity, isPending } = useGetIdentity();
  const [needsPassword, setNeedsPassword] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // 👈 Check if they are a new invited user who hasn't set a password
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.user_metadata?.is_invited_user === true) {
        setNeedsPassword(true);
      }
      setIsCheckingAuth(false);
    });
  }, []);

  const { data: contacts, isLoading: contactsLoading } = useGetList(
    "contacts",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "first_seen", order: "DESC" },
    },
  );

  const { data: deals, isLoading: dealsLoading } = useGetList("deals", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: tasks, isLoading: tasksLoading } = useGetList(
    "crm_activities",
    {
      pagination: { page: 1, perPage: 5 },
      sort: { field: "due_date", order: "ASC" },
      filter: { type: "task", "done_date@is": null },
    },
  );

  const { openDeals, wonDeals } = useMemo(() => {
    const open: any[] = [];
    const won: any[] = [];
    (deals ?? []).forEach((d: any) => {
      const stage = (d.stage || "").toLowerCase().trim();
      if (CLOSED_STAGES.has(stage)) won.push(d);
      else if (!LOST_STAGES.has(stage)) open.push(d);
    });
    return { openDeals: open, wonDeals: won };
  }, [deals]);

  const opportunityValue = openDeals.reduce(
    (sum, d: any) => sum + (Number(d.amount) || 0),
    0,
  );
  const stageProbabilities: Record<string, number> = {
    opportunity: 0.2,
    "proposal-sent": 0.4,
    "in-negotiation": 0.6,
    "in-negociation": 0.6,
    delayed: 0.3,
  };
  const weightedPipeline = openDeals.reduce((sum, deal: any) => {
    const stage = (deal.stage || "").toLowerCase().trim();
    const probability = stageProbabilities[stage] || 0.1;
    return sum + (Number(deal.amount) || 0) * probability;
  }, 0);

  const bookedValue = wonDeals.reduce(
    (sum, deal) => sum + (Number(deal.amount) || 0),
    0,
  );
  const conversionRate =
    opportunityValue > 0
      ? Math.round((bookedValue / opportunityValue) * 100)
      : 0;
  const openDealCount = openDeals.length;
  const wonDealCount = wonDeals.length;
  const totalCustomers = contacts?.length || 0;

  const pipelineMap = openDeals.reduce(
    (acc, deal: any) => {
      const stage = deal.stage || "Lead";
      if (!acc[stage]) acc[stage] = { count: 0, amount: 0 };
      acc[stage].count += 1;
      acc[stage].amount += Number(deal.amount) || 0;
      return acc;
    },
    {} as Record<string, { count: number; amount: number }>,
  );

  const pipeline = Object.entries(pipelineMap).map(([stage, data]) => ({
    stage,
    count: data.count,
    amount: data.amount,
  }));
  const stageOrder = new Map(dealStages.map((s, i) => [s.value, i]));
  const sortedPipeline = [...pipeline].sort((a, b) => {
    const ai = stageOrder.get(a.stage) ?? 999;
    const bi = stageOrder.get(b.stage) ?? 999;
    return ai - bi;
  });
  const maxPipelineAmount = Math.max(...pipeline.map((p) => p.amount), 1);

  const recentLeads =
    contacts?.slice(0, 100).map((contact) => ({
      id: contact.id,
      name:
        `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
        "Unknown",
      email: contact.email_jsonb?.[0]?.email || "No email",
      status: contact.status || "New",
      role: (contact as any).persona || "Champion",
      date: contact.created_at
        ? new Date(contact.created_at).toLocaleDateString()
        : "N/A",
    })) || [];

  if (isCheckingAuth) return <DashboardSkeleton />;

  // 👈 1,Force them to set password
  if (needsPassword) {
    return <Navigate to="/set-password" replace />;
  }

  // 👈2.  Force them to setup workspace if they don't have an org (normal signups)
  if (!isPending && identity && !identity.org_id) {
    return <Navigate to="/setup-workspace" replace />;
  }

  // 3. Force them to complete onboarding if they have an org but haven't done the wizard
  if (
    !isPending &&
    identity &&
    identity.org_id &&
    !identity.onboarding_completed_at
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (contactsLoading || dealsLoading || tasksLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-1xl font-bold">Dashboard</h1>
      </div>

      <DailyBriefing />

      {/* Mobile KPI strip */}
      <div className="md:hidden">
        <Card className="overflow-hidden">
          <CardContent className="p-0 divide-y divide-border">
            <StatRow
              icon={Users}
              label="Contacts"
              value={totalCustomers}
              tone="contacts"
              onClick={() => navigate("/contacts")}
            />
            <StatRow
              icon={FileText}
              label="Open Deals"
              value={openDealCount}
              tone="deals"
              onClick={() => navigate("/deals")}
            />
            <StatRow
              icon={DollarSign}
              label="Pipeline Value"
              value={`$${opportunityValue.toLocaleString()}`}
              tone="pipeline"
              onClick={() => navigate("/deals")}
            />
            <StatRow
              icon={TrendingUp}
              label="Won to Date"
              value={`$${bookedValue.toLocaleString()}`}
              tone="won"
            />
          </CardContent>
        </Card>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/contacts")}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Contacts</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCustomers}</div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/deals")}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Deals</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openDealCount}</div>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/deals")}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pipeline Value
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${opportunityValue.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <span className="font-semibold text-emerald-500">
                ${weightedPipeline.toLocaleString()}
              </span>{" "}
              weighted
            </p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/deals?filter={"stage":"won"}')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Won to Date</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${bookedValue.toLocaleString()}
            </div>
            <div className="flex items-center gap-2 text-sm mt-1">
              <span className="text-muted-foreground">
                {wonDealCount} won · {openDealCount} open
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {conversionRate}% conversion
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Avg. 45 days in Proposal
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Activities */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Activities</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => navigate("/crm_activities/create")}
                  variant="outline"
                  className="bg-white text-black hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/crm_activities")}
                >
                  View All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {tasks?.slice(0, 3).map((task) => (
              <div
                key={task.id}
                className="flex items-start gap-3 pb-3 border-b last:border-0"
              >
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium leading-snug line-clamp-2">
                    {task.text || "Untitled task"}
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    {task.priority && (
                      <Badge
                        variant={
                          task.priority === "high"
                            ? "destructive"
                            : task.priority === "medium"
                              ? "default"
                              : "secondary"
                        }
                      >
                        {task.priority}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Due: {formatDueDate(task.due_date, locale)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {(!tasks || tasks.length === 0) && (
              <p className="text-sm text-muted-foreground">No tasks found.</p>
            )}
          </CardContent>
        </Card>

        {/* Sales Pipeline */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Sales Pipeline</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="bg-white text-black hover:bg-gray-100"
                  size="sm"
                  onClick={() => navigate("/deals/create")}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Deal
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/deals")}
                >
                  View All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {sortedPipeline.length > 0 ? (
              sortedPipeline.map((item) => {
                const pct = Math.round((item.amount / maxPipelineAmount) * 100);
                return (
                  <div key={item.stage} className="space-y-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex items-baseline gap-1.5 min-w-0">
                        <span
                          className={`h-2 w-2 rounded-full shrink-0 ${stageColor(item.stage)}`}
                        />
                        <span className="text-sm font-medium capitalize truncate">
                          {item.stage.replace(/-/g, " ")}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          · {item.count} {item.count === 1 ? "deal" : "deals"}
                        </span>
                      </div>
                      <span className="text-sm font-semibold tabular-nums shrink-0">
                        ${(item.amount / 1000).toFixed(1)}k
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${stageColor(item.stage)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">
                No deals in pipeline.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Contacts */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Contacts</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/contacts")}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {recentLeads.slice(0, 3).map((lead) => (
              <div
                key={lead.id}
                className="flex flex-col border-b last:border-0 pb-3 last:pb-0 gap-1"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{lead.name}</p>
                    {lead.role !== "Contact" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 border-blue-500/30 text-blue-400 bg-blue-500/10 capitalize"
                      >
                        {lead.role}
                      </Badge>
                    )}
                  </div>
                  <Badge
                    variant={
                      lead.status === "Success" || lead.status === "Won"
                        ? "default"
                        : lead.status === "Processing" ||
                            lead.status === "Pending"
                          ? "secondary"
                          : "destructive"
                    }
                  >
                    {lead.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{lead.email}</p>
              </div>
            ))}
            {recentLeads.length === 0 && (
              <p className="text-sm text-muted-foreground">No leads yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <LeadsTable contacts={contacts || []} />
    </div>
  );
}

const KPI_COLORS: Record<string, { dot: string; value: string }> = {
  contacts: { dot: "bg-blue-500", value: "text-blue-500" },
  deals: { dot: "bg-violet-500", value: "text-violet-500" },
  pipeline: { dot: "bg-emerald-500", value: "text-emerald-500" },
  won: { dot: "bg-amber-500", value: "text-amber-500" },
};

function StatRow({ icon: Icon, label, value, tone, onClick }: { icon: React.ElementType; label: string; value: number | string; tone: keyof typeof KPI_COLORS; onClick?: () => void; }) {
  const colors = KPI_COLORS[tone];
  return (
    <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors" onClick={onClick}>
      <span className="flex items-center gap-3 text-sm text-muted-foreground min-w-0">
        <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", colors.dot)} />
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className={cn("text-base font-semibold tabular-nums shrink-0", colors.value)}>{value}</span>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between"><Skeleton className="h-10 w-48" /><div className="flex items-center gap-2"><Skeleton className="h-10 w-24" /><Skeleton className="h-10 w-24" /></div></div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => (<Card key={i}><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-4" /></CardHeader><CardContent><Skeleton className="h-8 w-20 mb-2" /><Skeleton className="h-4 w-32" /></CardContent></Card>))}</div>
    </div>
  );
}